import React, { useState, useCallback } from 'react';
import { Typography, Dialog, DialogContent, DialogTitle, Grid, CircularProgress } from '@mui/material';
import { withStyles } from 'tss-react/mui';
import { connect } from 'react-redux';
import { createActions } from '../pages/CreatePage/store';
import AppConstants from '../../App/AppConstants';
import ConfirmationDialog from './ConfirmationDialog';
import { DEFAULT_AUDIO_TRACK } from '../pages/CreatePage/CreatePageConstants';
import DialogCloseButton from '../buttons/DialogCloseButton';
import { trackEvent, AnalyticsConstants } from '../../utilities/analyticsUtilities';
import { appOperations } from '../../App/store';
import ColoredSnackbarConstants from '../misc/ColoredSnackbar/ColoredSnackbarConstants';
import CreatePageConstants from '../pages/CreatePage/CreatePageConstants';
import ExportOptionsList from './ExportOptionsList';
import UploadAudioSection from './UploadAudioSection';
import ExportActions from './ExportActions';

const styles = theme => ({
    dialog: {
        maxWidth: 380
    },
    content: {
        padding: 0,
        overflowY: 'hidden'
    },
    centerContainer: {
        padding: theme.spacing(4)
    }
});

const DEFAULT_PRO_MAX_QUEUE_SIZE = 10;
const DEFAULT_FREE_MAX_QUEUE_SIZE = 1;

const ExportDialog = React.memo(({
    classes,
    exportDialogOpen,
    setProperty,
    accountDetails,
    audioSettings,
    audioPreparing,
    queueingExport,
    savingInProgress,
    videoSettingsLoading,
    currentlyExporting,
    queuedExportsCount,
    videoSettings,
    backgroundVideosLoading,
    audioAnalyzing,
    presetsLoading,
    maxValues,
    queueExport,
    showMessage,
    save,
    complete,
    seePlans,
    applyNewAudioFile,
    isVideoExportsQueued,
    backgroundDimensions,
    showCriticalOperationProgressWarning
}) => {
    const [successOpen, setSuccessOpen] = useState(false);
    const [videoType, setVideoType] = useState(null);
    const [addCreditsOpen, setAddCreditsOpen] = useState(false);

    const duration = audioSettings.endTime - audioSettings.startTime;
    const isFreeUser = accountDetails.subscriptionType === AppConstants.FREE_SUBSCRIPTION_TYPE;

    const maxConcurrent = accountDetails?.maxQueueSize ?? (isFreeUser ? DEFAULT_FREE_MAX_QUEUE_SIZE : DEFAULT_PRO_MAX_QUEUE_SIZE);
    const allowedExporting = queuedExportsCount < maxConcurrent;
    const loadingAudioData = videoSettings.id && audioPreparing;

    const isLoading = [queueingExport, presetsLoading, savingInProgress, backgroundVideosLoading, loadingAudioData, audioAnalyzing].some(Boolean);
    const isDefaultAudio = audioSettings && audioSettings.url && audioSettings.url === DEFAULT_AUDIO_TRACK.SOUND_URL;
    const exportDisabled = !videoType || backgroundVideosLoading || successOpen || !audioSettings.url || queueingExport;

    const handleCloseDialog = useCallback(() => {
        setProperty('exportDialogOpen', false);
    }, [setProperty]);

    const handleVideoTypeChange = useCallback((newVideoType) => {
        setVideoType(newVideoType);
    }, []);

    const handleUpgradeClick = useCallback(() => {
        trackEvent('Clicked CTA', {
            category: AnalyticsConstants.ACQUISITION_CATEGORY,
            color: 'white',
            destination: 'plans',
            location: 'export dialog',
            text: 'OPTIONS',
            type: 'button'
        });

        if (typeof seePlans === 'function') seePlans();
    }, [seePlans]);

    const handleCreditsClick = useCallback(() => {
        if (
            accountDetails.subscriptionType === AppConstants.PROFESSIONAL_SUBSCRIPTION_TYPE ||
            accountDetails.subscriptionType === AppConstants.LIMITLESS_SUBSCRIPTION_TYPE
        ) {
            trackEvent('Clicked CTA', {
                category: AnalyticsConstants.ACQUISITION_CATEGORY,
                color: 'white',
                destination: 'add credits dialog',
                location: 'export dialog',
                text: 'ADD CREDITS',
                type: 'button'
            });

            setAddCreditsOpen(true);
        } else {
            trackEvent('Clicked CTA', {
                category: AnalyticsConstants.ACQUISITION_CATEGORY,
                color: 'white',
                destination: 'plans',
                location: 'export dialog',
                text: 'ADD CREDITS',
                type: 'button'
            });
            if (typeof seePlans === 'function') seePlans();
        }
    }, [accountDetails.subscriptionType, seePlans]);

    const handleAddCreditsClose = useCallback(() => {
        setAddCreditsOpen(false);
    }, []);

    const handleStartExport = useCallback(() => {
        if (accountDetails.subscriptionPaused) {
            showMessage("Unpause your subscription to continue.", ColoredSnackbarConstants.WARNING);
            return;
        }

        save(false, (videoId) => {
            queueExport(videoId, videoType, () => setSuccessOpen(true));
        });
    }, [accountDetails.subscriptionPaused, showMessage, save, queueExport, videoType]);

    const handleDurationButtonClick = useCallback(() => {
        setProperty('exportDialogOpen', false);
        setProperty('currentSettingsPanel', CreatePageConstants.SETTINGS_PANELS.AUDIO);
    }, [setProperty]);

    const handleSuccessClose = useCallback(() => {
        setSuccessOpen(false);
        if (typeof complete === 'function') complete();
        setProperty('exportDialogOpen', false);
    }, [complete, setProperty]);

    return (
        <>
            <Dialog
                open={exportDialogOpen}
                onClose={handleCloseDialog}
                fullWidth
                aria-labelledby='export-video-dialog-title'
                classes={{
                    paper: classes.dialog
                }}
            >
                <DialogTitle id='export-video-dialog-title'>
                    Export Video
                    <DialogCloseButton onClick={handleCloseDialog} />
                </DialogTitle>
                <DialogContent className={classes.content}>
                    {videoSettingsLoading ? (
                        <Grid 
                            container
                            justifyContent='center'
                            className={classes.centerContainer}
                        >
                            <CircularProgress />
                        </Grid>
                    ) : !allowedExporting ? (
                        <Grid 
                            container
                            justifyContent='center'
                            alignItems='center'
                            className={classes.centerContainer}
                            direction='column'
                        >
                            <Grid>
                                <Typography>
                                    {isFreeUser ? "Export in progress..." : "Export queue full"}
                                </Typography>
                            </Grid>
                            <br/>
                            <Grid>
                                <CircularProgress />
                            </Grid>
                        </Grid>
                    ) : ((audioSettings.url || audioSettings.displayFileName) && !isDefaultAudio) ? (
                        <ExportOptionsList
                            duration={duration}
                            videoType={videoType}
                            accountDetails={accountDetails}
                            maxValues={maxValues}
                            addCreditsOpen={addCreditsOpen}
                            backgroundDimensions={backgroundDimensions}
                            onVideoTypeChange={handleVideoTypeChange}
                            onUpgradeClick={handleUpgradeClick}
                            onCreditsClick={handleCreditsClick}
                            onAddCreditsClose={handleAddCreditsClose}
                        />
                    ) : (
                        <UploadAudioSection
                            audioPreparing={audioPreparing}
                            savingInProgress={savingInProgress}
                            audioAnalyzing={audioAnalyzing}
                            audioSettings={audioSettings}
                            isDefaultAudio={isDefaultAudio}
                            videoSettings={videoSettings}
                            accountDetails={accountDetails}
                            applyNewAudioFile={applyNewAudioFile}
                            showMessage={showMessage}
                            isVideoExportsQueued={isVideoExportsQueued}
                            showCriticalOperationProgressWarning={showCriticalOperationProgressWarning}
                        />
                    )}
                </DialogContent>
                <ExportActions
                    disabled={exportDisabled}
                    videoSettingsLoading={videoSettingsLoading}
                    allowedExporting={allowedExporting}
                    isDefaultAudio={isDefaultAudio}
                    isLoading={isLoading}
                    duration={duration}
                    onStartExport={handleStartExport}
                    onDurationButtonClick={handleDurationButtonClick}
                />
            </Dialog>
            <ConfirmationDialog
                title={'Video queued for export!'}
                open={successOpen}
                onClose={handleSuccessClose}
                hideCancel
            >
                {currentlyExporting ? "Your video will begin rendering after your other exports have completed. " : "Your video will begin rendering soon. "}
                You may close this page and come back later.{' '}
                {accountDetails.receiveVideoNotificationEmails ? 'We will email you when your export is finished. ' : null}
                {queuedExportsCount > 0 ? `You have ${queuedExportsCount} queued exports. ` : null} 
            </ConfirmationDialog>
        </>
    );
});

const mapStateToProps = (state) => {
    const { accountDetails } = state.app;
    const { videoSettings } = state.createPreview.present;
    const { savingInProgress } = state.create.videoSavingState;
    const { 
        videoSettingsLoading,
        audioSettings,
        exportDialogOpen,
        audioPreparing,
        queueingExport,
        audioAnalyzing,
        currentlyExporting,
        queuedExportsCount,
        runningExportsCount,
        presetsLoading,
        backgroundDimensions,
        backgroundVideosLoading,
        maxValues
    } = state.create;
    
    return {
        backgroundDimensions,
        videoSettingsLoading,
        videoSettings,
        savingInProgress,
        accountDetails,
        audioSettings,
        presetsLoading,
        exportDialogOpen,
        audioPreparing,
        queueingExport,
        audioAnalyzing,
        backgroundVideosLoading,
        currentlyExporting,
        queuedExportsCount,
        runningExportsCount,
        maxValues
    }
}

const mapDispatchToProps = (dispatch) => {
    const setProperty = (key, value) => dispatch(createActions.setProperty(key, value));
    const applyNewAudioFile = (videoId, file) => dispatch(createActions.applyNewAudioFile(videoId, file));
    const isVideoExportsQueued = () => dispatch(createActions.isVideoExportsQueued());
    const queueExport = (videoId, videoType, success) => dispatch(createActions.queueExport(videoId, videoType, success));
    const showMessage = (message, variant) => dispatch(appOperations.openSnack(message, variant));
    const showCriticalOperationProgressWarning = () => dispatch(createActions.showCriticalOperationProgressWarning());

    return {
        setProperty,
        applyNewAudioFile,
        queueExport,
        showMessage,
        isVideoExportsQueued,
        showCriticalOperationProgressWarning
    }
}

export default connect(mapStateToProps, mapDispatchToProps)(withStyles(ExportDialog, styles));